import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Layout from './Layout';
import ListPager from '../components/ListPager';

const API = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000';
const LIMIT = 20;
const OUTBOX_LIMIT = 30;

const FILTER_OPTIONS = [
  ['all_clinics', 'Всем клиникам'],
  ['all_doctors', 'Всем врачам'],
  ['active_doctors', 'Активным врачам'],
  ['doctor', 'Конкретному врачу'],
  ['clinic', 'Конкретной клинике'],
  ['network', 'Конкретной сети'],
];
const SEARCH_ENDPOINT = { doctor: '/admin/doctors', clinic: '/admin/clinics', network: '/admin/networks' };
const OUTBOX_STATUS_LABEL = { pending: 'Ожидает', sent: 'Отправлено', failed: 'Ошибка' };

function fmtDate(str) {
  if (!str) return '—';
  return new Date(str).toLocaleString('ru', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

export default function Notifications() {
  const [tab, setTab] = useState('send'); // 'send' | 'outbox'

  const [filter, setFilter] = useState('all_doctors');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [targetSearch, setTargetSearch] = useState('');
  const [targetOptions, setTargetOptions] = useState([]);
  const [target, setTarget] = useState(null); // { id, name }
  const [showConfirm, setShowConfirm] = useState(false);
  const [sending, setSending] = useState(false);
  const [msg, setMsg] = useState('');

  const [batches, setBatches] = useState([]);
  const [batchTotal, setBatchTotal] = useState(0);
  const [batchOffset, setBatchOffset] = useState(0);
  const [batchKind, setBatchKind] = useState('all'); // N-093: all | broadcast | auto
  const [expanded, setExpanded] = useState(null);
  const [expandedItems, setExpandedItems] = useState([]);

  const key = localStorage.getItem('adminKey');
  const headers = { 'X-Admin-Key': key };

  const isSpecific = ['doctor', 'clinic', 'network'].includes(filter);

  const fetchBatches = useCallback(() => {
    axios.get(`${API}/admin/notifications/batches`, { headers, params: { kind: batchKind, limit: LIMIT, offset: batchOffset } })
      .then(r => { setBatches(r.data.items); setBatchTotal(r.data.total); });
    // eslint-disable-next-line
  }, [batchOffset, batchKind]);
  useEffect(() => { if (tab === 'send') fetchBatches(); }, [fetchBatches, tab]);

  useEffect(() => {
    setTarget(null); setTargetSearch(''); setTargetOptions([]);
  }, [filter]);

  useEffect(() => {
    if (!isSpecific || !targetSearch.trim()) { setTargetOptions([]); return; }
    const t = setTimeout(() => {
      axios.get(`${API}${SEARCH_ENDPOINT[filter]}`, { headers, params: { search: targetSearch, limit: 8 } })
        .then(r => setTargetOptions(r.data.items));
    }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [targetSearch, filter]);

  const canSend = title.trim() && body.trim() && (!isSpecific || target);

  const send = async () => {
    setSending(true); setMsg('');
    try {
      const payload = { filter, title: title.trim(), body: body.trim() };
      if (isSpecific) payload.target_id = target.id;
      const r = await axios.post(`${API}/admin/notifications/send`, payload, { headers });
      setMsg(`Поставлено в очередь: ${r.data.recipients_count} получателей (${r.data.filter_label})`);
      setTitle(''); setBody(''); setTarget(null); setTargetSearch('');
      setShowConfirm(false);
      setBatchOffset(0); fetchBatches();
    } catch (e) { setMsg(e?.response?.data?.detail || 'Не удалось отправить'); }
    finally { setSending(false); }
  };

  const toggleExpand = (batchId) => {
    if (expanded === batchId) { setExpanded(null); return; }
    setExpanded(batchId);
    axios.get(`${API}/admin/notifications/batches/${batchId}`, { headers, params: { limit: 100 } })
      .then(r => setExpandedItems(r.data.items));
  };

  return (
    <Layout>
      <h1 style={styles.title}>Уведомления</h1>

      <div style={styles.tabs}>
        <div style={{ ...styles.tab, ...(tab === 'send' ? styles.tabActive : {}) }} onClick={() => setTab('send')}>Рассылки</div>
        <div style={{ ...styles.tab, ...(tab === 'outbox' ? styles.tabActive : {}) }} onClick={() => setTab('outbox')}>Очередь писем</div>
      </div>

      {tab === 'send' ? (
        <>
          <div style={styles.card}>
            <div style={styles.h2}>Новая рассылка</div>
            <div style={styles.formRow}>
              <div style={styles.fieldLabel}>Кому</div>
              <select style={styles.select} value={filter} onChange={e => setFilter(e.target.value)}>
                {FILTER_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>

            {isSpecific && (
              <div style={{ ...styles.formRow, position: 'relative' }}>
                <div style={styles.fieldLabel}>Поиск</div>
                <input
                  style={styles.input}
                  placeholder={filter === 'doctor' ? 'ФИО врача' : filter === 'clinic' ? 'Название клиники' : 'Название сети'}
                  value={target ? target.name : targetSearch}
                  onChange={e => { setTarget(null); setTargetSearch(e.target.value); }}
                />
                {!target && targetOptions.length > 0 && (
                  <div style={styles.dropdown}>
                    {targetOptions.map(o => (
                      <div key={o.id} style={styles.dropdownItem} onClick={() => { setTarget({ id: o.id, name: o.name }); setTargetOptions([]); }}>
                        {o.name}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div style={styles.formRow}>
              <div style={styles.fieldLabel}>Заголовок</div>
              <input style={styles.input} value={title} onChange={e => setTitle(e.target.value)} placeholder="Например: Обновление условий" />
            </div>
            <div style={styles.formRow}>
              <div style={styles.fieldLabel}>Текст</div>
              <textarea style={styles.textarea} rows={4} value={body} onChange={e => setBody(e.target.value)} placeholder="Текст уведомления" />
            </div>

            {msg && <div style={styles.msg}>{msg}</div>}
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button style={styles.btn} disabled={!canSend || sending} onClick={() => setShowConfirm(true)}>Отправить</button>
            </div>
          </div>

          <div style={styles.h2}>Лог отправок</div>
          <div style={styles.filterRow}>
            <select style={styles.select} value={batchKind} onChange={e => { setBatchKind(e.target.value); setBatchOffset(0); }}>
              <option value="all">Все</option>
              <option value="broadcast">Рассылки</option>
              <option value="auto">Автоматические</option>
            </select>
          </div>
          <div style={styles.table}>
            <div style={styles.thead}>
              <div style={styles.th}>Дата</div>
              <div style={styles.th}>Заголовок</div>
              <div style={styles.th}>Тип</div>
              <div style={styles.th}>Кому</div>
              <div style={styles.th}>Получателей</div>
              <div style={styles.th}>Прочитано</div>
            </div>
            {batches.map(b => (
              <div key={b.batch_id}>
                <div style={styles.row} onClick={() => toggleExpand(b.batch_id)}>
                  <div style={{ ...styles.td, color: '#64748b', fontSize: 12 }}>{new Date(b.created_at).toLocaleString('ru')}</div>
                  <div style={styles.td}>{b.title}</div>
                  <div style={styles.td}>
                    {b.is_broadcast
                      ? <span style={styles.badgeTeal}>Рассылка</span>
                      : <span style={styles.badgeGray}>Автоматическое</span>}
                  </div>
                  <div style={{ ...styles.td, color: '#64748b' }}>{b.filter_label || '—'}</div>
                  <div style={styles.td}>{b.total}</div>
                  <div style={styles.td}>{b.read_count} / {b.total}</div>
                </div>
                {expanded === b.batch_id && (
                  <div style={styles.expandBox}>
                    <div style={{ color: '#94a3b8', fontSize: 13, marginBottom: 10, whiteSpace: 'pre-wrap' }}>{b.body}</div>
                    {expandedItems.map(it => (
                      <div key={it.id} style={styles.recipientRow}>
                        <span>{it.kind}: {it.name}</span>
                        <span>{it.read_at ? <span style={styles.badgeGreen}>Прочитано</span> : <span style={styles.badgeGray}>Доставлено</span>}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
            {batches.length === 0 && <div style={styles.empty}>Рассылок пока не было</div>}
          </div>
          <ListPager total={batchTotal} limit={LIMIT} offset={batchOffset} onPage={setBatchOffset} />
        </>
      ) : (
        <EmailOutboxTab headers={headers} />
      )}

      {showConfirm && (
        <div style={styles.modalOverlay} onClick={() => setShowConfirm(false)}>
          <div style={styles.modal} onClick={e => e.stopPropagation()}>
            <h3 style={styles.modalTitle}>Отправить уведомление?</h3>
            <p style={styles.modalText}>
              Кому: <b style={{ color: '#e2e8f0' }}>{FILTER_OPTIONS.find(([v]) => v === filter)[1]}{target ? ` — ${target.name}` : ''}</b>
              <br />Заголовок: <b style={{ color: '#e2e8f0' }}>{title}</b>
            </p>
            <div style={styles.modalActions}>
              <button style={styles.btnGhost} onClick={() => setShowConfirm(false)}>Отмена</button>
              <button style={styles.btn} onClick={send} disabled={sending}>{sending ? 'Отправка…' : 'Отправить'}</button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}

// N-061: вкладка "Очередь писем" — визуализация email_outbox (защита от всплеска
// отправок при массовой рассылке — письма шлются фоновым воркером по одному за тик).
function EmailOutboxTab({ headers }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState({ pending: 0, sent: 0, failed: 0 });
  const [offset, setOffset] = useState(0);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [sortField, setSortField] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');

  const fetchData = useCallback(() => {
    axios.get(`${API}/admin/email-outbox`, {
      headers, params: {
        search: search || undefined, status: status || undefined,
        sort: sortField, order: sortDir, limit: OUTBOX_LIMIT, offset,
      },
    }).then(r => { setItems(r.data.items); setTotal(r.data.total); setCounts(r.data.status_counts); });
    // eslint-disable-next-line
  }, [search, status, sortField, sortDir, offset]);

  useEffect(() => {
    const t = setTimeout(fetchData, 250);
    return () => clearTimeout(t);
  }, [fetchData]);

  // Живая очередь — подтягиваем статусы раз в 5с, пока вкладка открыта.
  useEffect(() => {
    const t = setInterval(fetchData, 5000);
    return () => clearInterval(t);
  }, [fetchData]);

  const toggleSort = (field) => {
    setOffset(0);
    if (sortField === field) setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortField(field); setSortDir('asc'); }
  };
  const arrow = (field) => (sortField !== field ? '' : sortDir === 'asc' ? ' ▲' : ' ▼');
  const onFilter = (setter) => (e) => { setter(e.target.value); setOffset(0); };

  const badgeStyle = (s) => (s === 'sent' ? styles.badgeGreen : s === 'failed' ? styles.badgeRed : styles.badgeGray);

  return (
    <>
      <div style={styles.summaryRow}>
        <div style={styles.summaryTile}>
          <div style={styles.summaryNum}>{counts.pending}</div>
          <div style={styles.summaryLabel}>Ожидает</div>
        </div>
        <div style={styles.summaryTile}>
          <div style={{ ...styles.summaryNum, color: '#4ade80' }}>{counts.sent}</div>
          <div style={styles.summaryLabel}>Отправлено</div>
        </div>
        <div style={styles.summaryTile}>
          <div style={{ ...styles.summaryNum, color: '#f87171' }}>{counts.failed}</div>
          <div style={styles.summaryLabel}>Ошибка</div>
        </div>
      </div>

      <div style={styles.outboxTable}>
        <div style={styles.outboxThead}>
          <div style={{ ...styles.th, ...styles.thClickable }} onClick={() => toggleSort('created_at')}>В очереди с{arrow('created_at')}</div>
          <div style={{ ...styles.th, ...styles.thClickable }} onClick={() => toggleSort('recipient_name')}>Получатель{arrow('recipient_name')}</div>
          <div style={{ ...styles.th, ...styles.thClickable }} onClick={() => toggleSort('to_email')}>Email{arrow('to_email')}</div>
          <div style={{ ...styles.th, ...styles.thClickable }} onClick={() => toggleSort('subject')}>Тема{arrow('subject')}</div>
          <div style={{ ...styles.th, ...styles.thClickable }} onClick={() => toggleSort('status')}>Статус{arrow('status')}</div>
          <div style={{ ...styles.th, ...styles.thClickable }} onClick={() => toggleSort('sent_at')}>Отправлено{arrow('sent_at')}</div>
        </div>
        <div style={styles.outboxFilterRow}>
          <div />
          <input style={styles.filterInput} placeholder="Поиск по получателю/email/теме" value={search} onChange={onFilter(setSearch)} />
          <div />
          <div />
          <select style={styles.filterInput} value={status} onChange={onFilter(setStatus)}>
            <option value="">Все</option>
            <option value="pending">Ожидает</option>
            <option value="sent">Отправлено</option>
            <option value="failed">Ошибка</option>
          </select>
          <div />
        </div>
        {items.map(it => (
          <div key={it.id} style={styles.outboxRow} title={it.error || ''}>
            <div style={{ ...styles.td, color: '#64748b', fontSize: 12 }}>{fmtDate(it.created_at)}</div>
            <div style={styles.td}>{it.recipient_kind ? `${it.recipient_kind}: ${it.recipient_name}` : (it.recipient_name || '—')}</div>
            <div style={{ ...styles.td, color: '#64748b' }}>{it.to_email}</div>
            <div style={styles.td}>{it.subject}</div>
            <div style={styles.td}><span style={badgeStyle(it.status)}>{OUTBOX_STATUS_LABEL[it.status] || it.status}</span></div>
            <div style={{ ...styles.td, color: '#64748b', fontSize: 12 }}>{fmtDate(it.sent_at)}</div>
          </div>
        ))}
        {items.length === 0 && <div style={styles.empty}>Очередь пуста</div>}
      </div>
      <ListPager total={total} limit={OUTBOX_LIMIT} offset={offset} onPage={setOffset} />
    </>
  );
}

const styles = {
  title: { color: 'white', fontSize: 24, fontWeight: 700, margin: '0 0 20px' },
  tabs: { display: 'flex', gap: 4, marginBottom: 20, borderBottom: '1px solid #334155' },
  tab: { padding: '10px 18px', fontSize: 14, fontWeight: 600, color: '#64748b', cursor: 'pointer', borderBottom: '2px solid transparent' },
  tabActive: { color: '#2EC4B6', borderBottom: '2px solid #2EC4B6' },
  h2: { color: 'white', fontSize: 16, fontWeight: 600, margin: '24px 0 12px' },
  card: { background: '#1e293b', borderRadius: 12, padding: 20 },
  formRow: { marginBottom: 14 },
  fieldLabel: { fontSize: 12, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 },
  select: { width: '100%', boxSizing: 'border-box', background: '#0f172a', color: '#e2e8f0', border: '1px solid #334155', borderRadius: 6, padding: '9px 12px', fontSize: 14, outline: 'none', cursor: 'pointer' },
  input: { width: '100%', boxSizing: 'border-box', background: '#0f172a', color: '#e2e8f0', border: '1px solid #334155', borderRadius: 6, padding: '9px 12px', fontSize: 14, outline: 'none' },
  textarea: { width: '100%', boxSizing: 'border-box', background: '#0f172a', color: '#e2e8f0', border: '1px solid #334155', borderRadius: 6, padding: '9px 12px', fontSize: 14, outline: 'none', fontFamily: 'inherit', resize: 'vertical' },
  dropdown: { position: 'absolute', top: '100%', left: 0, right: 0, background: '#0f172a', border: '1px solid #334155', borderRadius: 6, marginTop: 4, zIndex: 20, maxHeight: 200, overflowY: 'auto' },
  dropdownItem: { padding: '8px 12px', fontSize: 13, color: '#e2e8f0', cursor: 'pointer' },
  msg: { color: '#2EC4B6', fontSize: 13, marginBottom: 12 },
  btn: { background: '#2EC4B6', color: '#0f172a', border: 'none', borderRadius: 6, padding: '9px 20px', fontSize: 14, fontWeight: 600, cursor: 'pointer' },
  btnGhost: { background: 'transparent', color: '#94a3b8', border: '1px solid #334155', borderRadius: 6, padding: '9px 20px', fontSize: 14, cursor: 'pointer' },
  filterRow: { display: 'flex', gap: 10, marginBottom: 10, maxWidth: 220 },
  table: { background: '#1e293b', borderRadius: 12, overflow: 'hidden' },
  thead: { display: 'grid', gridTemplateColumns: '1.2fr 1.8fr 1fr 1.3fr 0.9fr 0.9fr', padding: '12px 20px', borderBottom: '1px solid #334155' },
  th: { fontSize: 12, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 },
  thClickable: { cursor: 'pointer', userSelect: 'none' },
  row: { display: 'grid', gridTemplateColumns: '1.2fr 1.8fr 1fr 1.3fr 0.9fr 0.9fr', padding: '14px 20px', borderBottom: '1px solid #1e293b', background: '#0f172a', margin: '1px 0', cursor: 'pointer' },
  td: { fontSize: 14, color: '#e2e8f0', display: 'flex', alignItems: 'center' },
  expandBox: { background: '#0b1220', padding: '14px 24px', borderBottom: '1px solid #1e293b' },
  recipientRow: { display: 'flex', justifyContent: 'space-between', padding: '6px 0', fontSize: 13, color: '#e2e8f0', borderBottom: '1px solid #1e293b' },
  badgeGreen: { background: '#052e16', color: '#4ade80', padding: '2px 8px', borderRadius: 20, fontSize: 11 },
  badgeTeal: { background: '#042f2e', color: '#2EC4B6', padding: '2px 8px', borderRadius: 20, fontSize: 11, border: '1px solid #0f766e' },
  badgeGray: { background: '#1e293b', color: '#64748b', padding: '2px 8px', borderRadius: 20, fontSize: 11, border: '1px solid #334155' },
  badgeRed: { background: '#450a0a', color: '#f87171', padding: '2px 8px', borderRadius: 20, fontSize: 11 },
  empty: { color: '#64748b', fontSize: 14, textAlign: 'center', padding: '32px 0' },
  modalOverlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50 },
  modal: { background: '#1e293b', borderRadius: 12, padding: 24, width: 440, maxWidth: '90vw', border: '1px solid #334155' },
  modalTitle: { color: 'white', fontSize: 18, fontWeight: 700, margin: '0 0 12px' },
  modalText: { color: '#94a3b8', fontSize: 13, lineHeight: 1.6, margin: '0 0 16px' },
  modalActions: { display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 18 },
  summaryRow: { display: 'flex', gap: 12, marginBottom: 20 },
  summaryTile: { background: '#1e293b', borderRadius: 12, padding: '16px 24px', flex: 1, textAlign: 'center' },
  summaryNum: { fontSize: 26, fontWeight: 700, color: '#e2e8f0' },
  summaryLabel: { fontSize: 12, color: '#64748b', marginTop: 4, textTransform: 'uppercase', letterSpacing: 0.5 },
  outboxTable: { background: '#1e293b', borderRadius: 12, overflow: 'hidden' },
  outboxThead: { display: 'grid', gridTemplateColumns: '1.3fr 1.6fr 1.8fr 2fr 1fr 1.3fr', padding: '12px 20px', borderBottom: '1px solid #334155' },
  outboxFilterRow: { display: 'grid', gridTemplateColumns: '1.3fr 1.6fr 1.8fr 2fr 1fr 1.3fr', padding: '8px 20px', borderBottom: '1px solid #334155', background: '#172033', gap: 8 },
  filterInput: { width: '100%', background: '#0f172a', color: '#e2e8f0', border: '1px solid #334155', borderRadius: 6, padding: '5px 8px', fontSize: 12, outline: 'none', boxSizing: 'border-box' },
  outboxRow: { display: 'grid', gridTemplateColumns: '1.3fr 1.6fr 1.8fr 2fr 1fr 1.3fr', padding: '14px 20px', borderBottom: '1px solid #1e293b', background: '#0f172a', margin: '1px 0' },
};
